import React, { createContext, useContext, useState, useEffect } from 'react'
import { shopify } from '../lib/shopifyClient'
import ls from 'local-storage'
import { debugCart } from '../utils/debugCart'
import { debugSubscription } from '../utils/debugSubscription'
import { CART_FIELDS } from '../lib/shopifyQueries'

const CartContext = createContext()

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    // Try to load cart from localStorage on initialization
    const savedCartId = ls('lastCartID')
    const savedCartData = ls('cartData')
    
    // If we have saved cart data, use it for immediate display
    if (savedCartData && savedCartId) {
      return savedCartData
    }
    
    return savedCartId ? { id: savedCartId, lines: [] } : null
  })
  const [status, setStatus] = useState('idle')
  
  // Helper to save cart data
  const saveCartData = (cartData) => {
    setCart(cartData)
    if (cartData && cartData.id) {
      ls('lastCartID', cartData.id)
      ls('cartData', cartData)
    } else {
      ls.remove('lastCartID')
      ls.remove('cartData')
    }
  }

  // Fetch cart data on mount if we have a saved cart ID
  useEffect(() => {
    debugCart.logConfig(); // Log configuration on mount
    const fetchCart = async () => {
      const savedCartId = ls('lastCartID')
      if (savedCartId && !cart?.lines?.length) {
        // Only fetch if we don't already have cart data
        try {
          const query = `
            query GetCart($id: ID!) {
              cart(id: $id) {
                ${CART_FIELDS}
              }
            }
          `
          const { cart: fetchedCart } = await shopify.request(query, { id: savedCartId })
          if (fetchedCart) {
            const cartData = {
              id: fetchedCart.id,
              checkoutUrl: fetchedCart.checkoutUrl,
              lines: fetchedCart.lines.edges.map(e => e.node),
              totalQuantity: fetchedCart.totalQuantity,
              cost: fetchedCart.cost
            }
            saveCartData(cartData)
            debugCart.logCartState(cartData)
          } else {
            // Cart might have been checked out, clear it
            console.log('Cart not found, might have been checked out')
            saveCartData(null)
          }
        } catch (error) {
          console.error('Error fetching cart:', error)
          // Clear invalid cart ID
          saveCartData(null)
        }
      }
    }
    fetchCart()
  }, [])
  

  const createCart = async () => {
    try {
      const mutation = `
        mutation CartCreate($input: CartInput) {
          cartCreate(input: $input) {
            cart {
              ${CART_FIELDS}
            }
          }
        }
      `
      const { cartCreate } = await shopify.request(mutation, { input: {} })
      const newCart = { 
        id: cartCreate.cart.id, 
        checkoutUrl: cartCreate.cart.checkoutUrl,
        lines: cartCreate.cart.lines.edges.map(e => e.node),
        totalQuantity: cartCreate.cart.totalQuantity,
        cost: cartCreate.cart.cost
      }
      saveCartData(newCart)
      return newCart
    } catch (error) {
      console.error('Error creating cart:', error)
      throw error
    }
  }

  const addLine = async (line) => {
    try {
      setStatus('loading')
      const c = cart || await createCart()
      
      // Debug the line being added
      debugSubscription.logCartLine(line);
      
      const mutation = `
        mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              ${CART_FIELDS}
            }
            userErrors {
              field
              message
            }
          }
        }
      `
      const variables = { cartId: c.id, lines: [line] }
      
      // Debug the mutation
      debugSubscription.logMutation(mutation, variables);
      
      const data = await shopify.request(mutation, variables)
      
      // Debug the response
      debugSubscription.logResponse(data);
      
      // Check if any errors in response
      if (data.cartLinesAdd?.userErrors?.length > 0) {
        console.error('Cart API Errors:', data.cartLinesAdd.userErrors);
      }
      const updatedCart = { 
        id: c.id, 
        checkoutUrl: data.cartLinesAdd.cart.checkoutUrl, 
        lines: data.cartLinesAdd.cart.lines.edges.map((e)=>e.node),
        totalQuantity: data.cartLinesAdd.cart.totalQuantity,
        cost: data.cartLinesAdd.cart.cost
      }
      saveCartData(updatedCart)
      debugCart.logCartState(updatedCart)
      setStatus('idle')
    } catch (error) {
      debugCart.logError('Error adding line to cart', error)
      setStatus('error')
      throw error
    }
  }

  const updateLine = async (lineId, quantity) => {
    if (!cart) return
    setStatus('loading')
    const mutation = `
      mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            ${CART_FIELDS}
          }
        }
      }
    `
    const data = await shopify.request(mutation, { cartId: cart.id, lines: [{ id: lineId, quantity }] })
    const updatedCart = {
      ...cart,
      checkoutUrl: data.cartLinesUpdate.cart.checkoutUrl,
      lines: data.cartLinesUpdate.cart.lines.edges.map((e) => e.node),
      totalQuantity: data.cartLinesUpdate.cart.totalQuantity,
      cost: data.cartLinesUpdate.cart.cost
    }
    saveCartData(updatedCart)
    setStatus('idle')
  }

  const removeLine = async (lineId) => {
    if (!cart) return
    setStatus('loading')
    const mutation = `
      mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            ${CART_FIELDS}
          }
        }
      }
    `
    const data = await shopify.request(mutation, { cartId: cart.id, lineIds: [lineId] })
    const updatedCart = {
      ...cart,
      checkoutUrl: data.cartLinesRemove.cart.checkoutUrl,
      lines: data.cartLinesRemove.cart.lines.edges.map((e) => e.node),
      totalQuantity: data.cartLinesRemove.cart.totalQuantity,
      cost: data.cartLinesRemove.cart.cost
    }
    saveCartData(updatedCart)
    setStatus('idle')
  }

  const checkout = () => {
    if (cart?.checkoutUrl) {
      console.log('=== CHECKOUT CLICKED ===');
      console.log('Cart state:', cart);
      console.log('Cart lines with selling plans:');
      cart.lines?.forEach((line, index) => {
        console.log(`Line ${index + 1}:`, {
          product: line.merchandise?.product?.title,
          sellingPlan: line.sellingPlanAllocation?.sellingPlan,
          attributes: line.attributes
        });
      });
      
      // Verify selling plans match expected frequencies
      debugSubscription.verifyCheckout(cart);
      
      console.log('Checkout URL:', cart.checkoutUrl);
      
      // Don't clear cart here - let it persist until confirmed checkout
      window.location.href = cart.checkoutUrl
    }
  }
  
  const clearCart = () => {
    saveCartData(null)
  }

  return (
    <CartContext.Provider
      value={{
        cart,
        status,
        addLine,
        updateLine,
        removeLine,
        checkout,
        clearCart,
        totalQuantity: cart?.totalQuantity || 0
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export default CartProvider